import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { onAuthStateChanged, type User } from "firebase/auth";
import { auth } from "./firebase";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Setup from "./pages/Setup";
import Providers from "./pages/Providers";
import Tools from "./pages/Tools";
import Files from "./pages/Files";
import Teams from "./pages/Teams";
import Consent from "./pages/Consent";

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  // Distinct from `user === null`: on first paint we do not yet know whether a session
  // exists, and flashing the login screen at an already-signed-in user looks broken.
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setResolving(false);
    });
  }, []);

  if (resolving) {
    return (
      <div className="boot">
        <div className="spinner" />
      </div>
    );
  }

  /*
   * Signed out, every path is the login screen -- no redirect to /login, so the address
   * the user arrived at survives the sign-in and they land where they were headed.
   *
   * Signed in, Dashboard is the shell (top bar, nav, the data both pages share) and the
   * pages render into its outlet. These are real paths, not tabs: /providers is a link
   * you can send someone, and the back button walks the pages. Firebase Hosting already
   * rewrites unmatched paths to index.html, so a deep link survives a reload.
   */
  if (!user) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<Login />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* OAuth consent stands outside the Dashboard shell: it is a focused yes/no
            question that ends in a redirect off-site, and loading the whole dashboard's
            data behind it would be wasted work on a page the user leaves in seconds.
            Signed out, the block above shows Login at this same URL, so the redirect
            from /oauth/authorize survives the sign-in and lands back here. */}
        <Route path="/consent" element={<Consent user={user} />} />
        <Route element={<Dashboard user={user} />}>
          <Route path="/setup" element={<Setup />} />
          <Route path="/providers" element={<Providers />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/files" element={<Files />} />
          <Route path="/teams" element={<Teams />} />
          {/* Setup is the landing page: a new user has to install the server before
              anything on Providers can do anything for them. */}
          <Route path="*" element={<Navigate to="/setup" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
